import React, { useState } from 'react';
import { faCalendar, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/ro';
import { useTranslation } from 'react-i18next';
import { Task } from '../../types';
import { TaskScheduleModal } from '../task-schedule-modal/TaskScheduleModal';

type ScheduleValues = Pick<Task, 'dueDate' | 'schedule' | 'reminderTime' | 'recurrence'>;

interface AddTaskModalProps {
    editingTask: Task | null;
    endDateTime: Date | null;
    startDateTime: Date | null;
    isFlexibleTask: boolean;
    flexiblePrecision: Exclude<NonNullable<Task['schedule']>['precision'], 'hour'>;
    flexibleEndDate: Date | null;
    newTaskTitle: string;
    onTitleChange: (title: string) => void;
    onSubmit: (schedule: ScheduleValues) => void;
    onCancel: () => void;
}

const createScheduleValues = (
    editingTask: Task | null,
    startDateTime: Date | null,
    endDateTime: Date | null,
    isFlexibleTask: boolean,
    flexiblePrecision: AddTaskModalProps['flexiblePrecision'],
    flexibleEndDate: Date | null,
): ScheduleValues => {
    if (editingTask) {
        return {
            dueDate: editingTask.dueDate,
            schedule: editingTask.schedule,
            reminderTime: editingTask.reminderTime,
            recurrence: editingTask.recurrence,
        };
    }

    if (!startDateTime) return {};

    const end = isFlexibleTask
        ? flexibleEndDate || startDateTime
        : endDateTime || startDateTime;
    return {
        dueDate: startDateTime.toISOString(),
        schedule: {
            precision: isFlexibleTask ? flexiblePrecision : 'hour',
            kind: 'range',
            startDate: startDateTime.toISOString(),
            endDate: end.toISOString(),
        },
    };
};

const formatSchedule = (values: ScheduleValues, language: string, noDateLabel: string) => {
    if (!values.dueDate) return noDateLabel;

    const options: Intl.DateTimeFormatOptions = values.schedule?.precision === 'hour'
        ? {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }
        : values.schedule?.precision === 'month'
            ? { month: 'short', year: 'numeric' }
            : values.schedule?.precision === 'year'
                ? { year: 'numeric' }
                : { day: 'numeric', month: 'short', year: 'numeric' };
    const start = new Date(values.schedule?.startDate || values.dueDate).toLocaleString(language, options);
    const endDate = values.schedule?.endDate;

    if (!endDate || values.schedule?.kind !== 'range') return start;

    const end = new Date(endDate).toLocaleString(language, options);
    return `${start} – ${end}`;
};

export const AddTaskModal: React.FC<AddTaskModalProps> = ({
    editingTask,
    endDateTime,
    startDateTime,
    isFlexibleTask,
    flexiblePrecision,
    flexibleEndDate,
    newTaskTitle,
    onTitleChange,
    onSubmit,
    onCancel,
}) => {
    const { t, i18n } = useTranslation();
    const [showScheduleEditor, setShowScheduleEditor] = useState(false);
    const [scheduleValues, setScheduleValues] = useState(() => createScheduleValues(
        editingTask,
        startDateTime,
        endDateTime,
        isFlexibleTask,
        flexiblePrecision,
        flexibleEndDate,
    ));

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language === 'ro' ? 'ro' : 'en'}>
            <div className="add-task-modal" onClick={onCancel} role="presentation">
                <div
                    className="add-task-content"
                    onClick={(event) => event.stopPropagation()}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="planner-task-modal-title"
                >
                    <div className="add-task-header">
                        <h3 id="planner-task-modal-title">
                            {editingTask ? t('dailyPlanner.editTask') : t('dailyPlanner.addTask')}
                        </h3>
                        <button
                            type="button"
                            className="add-task-close"
                            onClick={onCancel}
                            aria-label={t('dailyPlanner.cancel')}
                        >
                            <FontAwesomeIcon icon={faXmark} />
                        </button>
                    </div>
                    <input
                        type="text"
                        value={newTaskTitle}
                        onChange={(event) => onTitleChange(event.target.value)}
                        placeholder={t('dailyPlanner.taskTitlePlaceholder')}
                        className="add-task-input"
                        autoFocus
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') onSubmit(scheduleValues);
                            if (event.key === 'Escape') onCancel();
                        }}
                    />
                    <button
                        type="button"
                        className="add-task-schedule-link"
                        onClick={() => setShowScheduleEditor(true)}
                    >
                        <FontAwesomeIcon icon={faCalendar} />
                        <span>{formatSchedule(scheduleValues, i18n.language, t('addTask.noDueDate'))}</span>
                    </button>
                    <div className="add-task-buttons">
                        <button type="button" onClick={onCancel} className="cancel-button">
                            {t('dailyPlanner.cancel')}
                        </button>
                        <button
                            type="button"
                            onClick={() => onSubmit(scheduleValues)}
                            className="confirm-button"
                        >
                            {editingTask ? t('dailyPlanner.save') : t('dailyPlanner.add')}
                        </button>
                    </div>
                </div>
            </div>
            {showScheduleEditor && (
                <TaskScheduleModal
                    dueDate={scheduleValues.dueDate ? dayjs(scheduleValues.dueDate) : null}
                    schedule={scheduleValues.schedule}
                    reminderTime={scheduleValues.reminderTime ? dayjs(scheduleValues.reminderTime) : null}
                    recurrence={scheduleValues.recurrence}
                    onDueDateChange={(value) => setScheduleValues((current) => ({
                        ...current,
                        dueDate: value?.toISOString(),
                    }))}
                    onScheduleChange={(schedule) => setScheduleValues((current) => ({
                        ...current,
                        schedule,
                    }))}
                    onReminderTimeChange={(value) => setScheduleValues((current) => ({
                        ...current,
                        reminderTime: value?.toISOString(),
                    }))}
                    onRecurrenceChange={(recurrence) => setScheduleValues((current) => ({
                        ...current,
                        recurrence,
                    }))}
                    onClose={() => setShowScheduleEditor(false)}
                />
            )}
        </LocalizationProvider>
    );
};
